import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { API_URL } from "../config/network";

const AuthContext = createContext(null);

const readStoredToken = () => {
  const raw = localStorage.getItem("wavvy_token");
  if (!raw || raw === "undefined" || raw === "null") return "";
  return raw;
};

const readStoredUser = () => {
  const raw = localStorage.getItem("wavvy_user");
  if (!raw || raw === "undefined" || raw === "null") return null;
  try {
    return JSON.parse(raw);
  } catch (_error) {
    localStorage.removeItem("wavvy_user");
    return null;
  }
};

const readJsonSafe = async (response) => {
  try {
    return await response.json();
  } catch (_error) {
    return {};
  }
};

const isOffline = () => (typeof navigator !== "undefined" ? navigator.onLine === false : false);

const requestWithRetry = async (url, options, retries = 1) => {
  if (isOffline()) {
    const error = new TypeError("Offline");
    error.code = "OFFLINE";
    throw error;
  }

  try {
    return await fetch(url, options);
  } catch (error) {
    if (retries <= 0 || isOffline()) throw error;
    await new Promise((resolve) => setTimeout(resolve, 450));
    return requestWithRetry(url, options, retries - 1);
  }
};

export function AuthProvider({ children }) {
  const [token, setToken] = useState(readStoredToken);
  const [user, setUser] = useState(readStoredUser);

  const updateUser = useCallback((nextUser) => {
    setUser(nextUser);
    if (nextUser) {
      localStorage.setItem("wavvy_user", JSON.stringify(nextUser));
    } else {
      localStorage.removeItem("wavvy_user");
    }
  }, []);

  const persist = useCallback((nextToken, nextUser) => {
    setToken(nextToken);
    updateUser(nextUser);
    localStorage.setItem("wavvy_token", nextToken);
  }, [updateUser]);

  const clear = useCallback(() => {
    setToken("");
    updateUser(null);
    localStorage.removeItem("wavvy_token");
    localStorage.removeItem("wavvy_user");
    localStorage.removeItem("wavvy_settings");
    localStorage.removeItem("wavvy_lang");
  }, [updateUser]);

  const login = useCallback(async (email, password) => {
    try {
      const res = await requestWithRetry(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });
      const data = await readJsonSafe(res);
      if (!res.ok) {
        return {
          ok: false,
          code: data.code,
          error: data.message || "Login failed"
        };
      }
      persist(data.token, data.user);
      return { ok: true };
    } catch (err) {
      if (err?.code === "OFFLINE") {
        return { ok: false, error: "You are offline. Check your internet connection and try again." };
      }
      if (err instanceof TypeError) {
        return { ok: false, error: "Unable to contact server. Is the backend running?" };
      }
      return { ok: false, error: err?.message || "Login failed" };
    }
  }, [persist]);

  const register = useCallback(async (username, email, password) => {
    try {
      const res = await requestWithRetry(`${API_URL}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, email, password })
      });
      const data = await readJsonSafe(res);
      if (!res.ok) return { ok: false, error: data.message || "Registration failed" };
      return {
        ok: true,
        requiresEmailVerification: Boolean(data.requiresEmailVerification ?? true)
      };
    } catch (err) {
      if (err?.code === "OFFLINE") {
        return { ok: false, error: "You are offline. Check your internet connection and try again." };
      }
      if (err instanceof TypeError) {
        return { ok: false, error: "Unable to contact server. Is the backend running?" };
      }
      return { ok: false, error: err?.message || "Registration failed" };
    }
  }, []);

  const resendVerification = useCallback(async (email) => {
    try {
      const res = await requestWithRetry(`${API_URL}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      const data = await readJsonSafe(res);
      if (!res.ok) return { ok: false, error: data.message || "Could not resend verification email" };
      return { ok: true, message: data.message || "Verification email sent" };
    } catch (err) {
      if (err?.code === "OFFLINE") {
        return { ok: false, error: "You are offline. Check your internet connection and try again." };
      }
      if (err instanceof TypeError) {
        return { ok: false, error: "Unable to contact server. Is the backend running?" };
      }
      return { ok: false, error: err?.message || "Could not resend verification email" };
    }
  }, []);

  const verifyEmail = useCallback(async (token) => {
    if (!token) return { ok: false, error: "Verification token is missing" };

    try {
      const postRes = await requestWithRetry(`${API_URL}/auth/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token })
      });

      if (postRes.ok) {
        const data = await readJsonSafe(postRes);
        return { ok: true, message: data.message || "Email verified successfully" };
      }

      if (postRes.status !== 404 && postRes.status !== 405) {
        const data = await readJsonSafe(postRes);
        return { ok: false, error: data.message || "Email verification failed" };
      }

      const getRes = await requestWithRetry(`${API_URL}/auth/verify-email?token=${encodeURIComponent(token)}`, undefined);
      const data = await readJsonSafe(getRes);
      if (!getRes.ok) return { ok: false, error: data.message || "Email verification failed" };
      return { ok: true, message: data.message || "Email verified successfully" };
    } catch (err) {
      if (err?.code === "OFFLINE") {
        return { ok: false, error: "You are offline. Check your internet connection and try again." };
      }
      if (err instanceof TypeError) {
        return { ok: false, error: "Unable to contact server. Is the backend running?" };
      }
      return { ok: false, error: err?.message || "Email verification failed" };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      if (token) {
        await requestWithRetry(`${API_URL}/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } finally {
      clear();
      if (typeof window !== "undefined") {
        window.location.replace("/auth/login");
      }
    }
  }, [clear, token]);

  const value = useMemo(
    () => ({
      token,
      user,
      login,
      register,
      resendVerification,
      verifyEmail,
      logout,
      setUser: updateUser
    }),
    [token, user, login, register, resendVerification, verifyEmail, logout, updateUser]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
